import { useAuth0 } from '@auth0/auth0-react';
import { Box, ChakraProvider, Flex } from '@chakra-ui/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Navigate, Outlet, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';

import { AnalyticsBoot } from './components/analytics/AnalyticsBoot';
import { FollowUpSchedulingGuard } from './components/tcm/FollowUpSchedulingGuard';
import { normalizeDischargeDate } from './modules/tcm/utils/dischargeDateUtils';
import { DeploymentDetector } from './components/deployment/DeploymentDetector';
import { DeploymentNotification } from './components/deployment/DeploymentNotification';
import WithSubnavigation from './components/layout/nav-bar/TopNav';
import PageLoader from './components/layout/page-loader/PageLoader';
import { RoutePermissionGuard } from './components/permission-guard/RoutePermissionGuard';
import { SettingsProvider } from './context';
import { AuthenticationGuard } from './pages/auth/AuthenticationGuard';
import CookiePolicyPage from './pages/cookie-policy/CookiePolicyPage';
import Home from './pages/home/Home';
import LoginPage from './pages/login/LoginPage';
import NotFoundPage from './pages/not-found/NotFound';
import PrivacyPolicyPage from './pages/privacy-policy/PrivacyPolicyPage';
import ProfilePage from './pages/profile/Profile';
import ResourceMatrixSearch from './pages/resource-matrix-search/ResourceMatrixSearch';
import MarketDetails from './pages/resource-matrix/market/MarketDetails';
import CreateMarket from './pages/resource-matrix/markets/CreateMarket';
import MarketPracticeManagement from './pages/resource-matrix/markets/MarketPracticeManagement';
import { BulkEditPlans } from './pages/resource-matrix/plans/BulkEditPlans';
import CreatePlan from './pages/resource-matrix/plans/CreatePlan';
import EditPlanGroup from './pages/resource-matrix/plans/EditPlanGroup';
import PlanDetails from './pages/resource-matrix/plans/PlanDetails';
import PlansList from './pages/resource-matrix/plans/PlansList';
import CreatePractice from './pages/resource-matrix/practices/CreatePractice';
import { PracticeDetail } from './pages/resource-matrix/practices/PracticeDetail';
import PracticesPage from './pages/resource-matrix/practices/PracticeList';
import CreateProvider from './pages/resource-matrix/providers/CreateProvider';
import { ProviderDetail } from './pages/resource-matrix/providers/ProviderDetail';
import ProviderList from './pages/resource-matrix/providers/ProvidersList';
import StateDetailPage from './pages/resource-matrix/state/StateDetails';
import UnifiedSearchResults from './pages/resource-matrix/unified-search-results/UnifiedSearchResults';
import SettingsPage from './pages/settings/SettingsPage';
import { TcmDirectEntry, TcmModuleEntry } from './modules/tcm/TcmModuleEntry';
import { theme } from './theme/theme';
import {
  pageVariants,
  subtleTransition,
} from './utils/motion';

function Dashboard() {
  const { isLoading, error, isAuthenticated } = useAuth0();
  const location = useLocation();
  const isRootPath = location.pathname === '/';
  const prefersReducedMotion = useReducedMotion();

  if (isLoading) {
    return (
      <Box>
        <WithSubnavigation />
        <PageLoader height="calc(100vh - 4rem)" />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  if (error) {
    return (
      <div>
        <WithSubnavigation />
        <div>Oops...{error.message}</div>
        <button onClick={() => window.location.reload()}>
          Reload Page
        </button>
      </div>
    );
  }

  return (
    <Box>
      <WithSubnavigation />
      <Flex>
        <Box flex="1" minW="0">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              variants={prefersReducedMotion ? undefined : pageVariants}
              initial={prefersReducedMotion ? false : 'initial'}
              animate={prefersReducedMotion ? undefined : 'animate'}
              exit={prefersReducedMotion ? undefined : 'exit'}
              transition={prefersReducedMotion ? undefined : subtleTransition}
            >
              {isRootPath && <Home />}
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </Box>
      </Flex>
      {/* Debug components - comment out in production */}
      {/* <AuthDebugger /> */}
      {/* <EnvDebugger /> */}
    </Box>
  );
}

function FollowUpSchedulingRoute() {
  return (
    <FollowUpSchedulingGuard>
      <TcmModuleEntry />
    </FollowUpSchedulingGuard>
  );
}

function FollowUpSchedulingDirectRoute() {
  const { tablespace, athenaPatientId } = useParams<{
    tablespace: string
    athenaPatientId: string
  }>()
  const [searchParams] = useSearchParams()
  const dischargeDate = normalizeDischargeDate(searchParams.get('discharge_date') ?? '')

  if (!tablespace || !athenaPatientId) {
    return <Navigate to="/follow-up-scheduling" replace />
  }

  return (
    <FollowUpSchedulingGuard>
      <TcmDirectEntry
        tablespace={tablespace}
        athenaPatientId={athenaPatientId}
        dischargeDate={dischargeDate}
      />
    </FollowUpSchedulingGuard>
  )
}

export default function App() {
  return (
    <ChakraProvider theme={theme}>
      <SettingsProvider>
        <DeploymentDetector>
          <AnalyticsBoot />
          <Routes>
            <Route path="/" element={<Dashboard />}>
                  <Route
                    path="resource-matrix-search"
                    element={
                      <AuthenticationGuard
                        component={ResourceMatrixSearch}
                      />
                    }
                  />
                  <Route
                    path="follow-up-scheduling/direct/:tablespace/:athenaPatientId"
                    element={
                      <AuthenticationGuard component={FollowUpSchedulingDirectRoute} />
                    }
                  />
                  <Route
                    path="follow-up-scheduling"
                    element={
                      <AuthenticationGuard component={FollowUpSchedulingRoute} />
                    }
                  />
                  <Route
                    path="tcm"
                    element={<Navigate to="/follow-up-scheduling" replace />}
                  />
                  <Route
                    path="practices"
                    element={
                      <AuthenticationGuard
                        component={PracticesPage}
                      />
                    }
                  />
                  <Route
                    path="practices/:id"
                    element={
                      <AuthenticationGuard
                        component={PracticeDetail}
                      />
                    }
                  />
                  <Route
                    path="practices/edit/:id"
                    element={
                      <AuthenticationGuard
                        component={() => (
                          <RoutePermissionGuard requiredPermission="canEditPractices">
                            <CreatePractice />
                          </RoutePermissionGuard>
                        )}
                      />
                    }
                  />
                  <Route
                    path="practices/create"
                    element={
                      <AuthenticationGuard
                        component={() => (
                          <RoutePermissionGuard requiredPermission="canEditPractices">
                            <CreatePractice />
                          </RoutePermissionGuard>
                        )}
                      />
                    }
                  />
                  <Route
                    path="providers"
                    element={
                      <AuthenticationGuard component={ProviderList} />
                    }
                  />
                  <Route
                    path="providers/:id"
                    element={
                      <AuthenticationGuard
                        component={ProviderDetail}
                      />
                    }
                  />
                  <Route
                    path="providers/create"
                    element={
                      <AuthenticationGuard
                        component={() => (
                          <RoutePermissionGuard requiredPermission="canEditProviders">
                            <CreateProvider />
                          </RoutePermissionGuard>
                        )}
                      />
                    }
                  />
                  <Route
                    path="providers/edit/:id"
                    element={
                      <AuthenticationGuard
                        component={() => (
                          <RoutePermissionGuard requiredPermission="canEditProviders">
                            <CreateProvider />
                          </RoutePermissionGuard>
                        )}
                      />
                    }
                  />
                  <Route
                    path="plans"
                    element={
                      <AuthenticationGuard component={PlansList} />
                    }
                  />
                  <Route
                    path="markets/:id"
                    element={
                      <AuthenticationGuard
                        component={MarketDetails}
                      />
                    }
                  />
                  <Route
                    path="markets/create"
                    element={
                      <AuthenticationGuard
                        component={() => (
                          <RoutePermissionGuard requiredPermission="canEditInsurance">
                            <CreateMarket />
                          </RoutePermissionGuard>
                        )}
                      />
                    }
                  />
                  <Route
                    path="markets/edit/:id"
                    element={
                      <AuthenticationGuard
                        component={() => (
                          <RoutePermissionGuard requiredPermission="canEditInsurance">
                            <CreateMarket />
                          </RoutePermissionGuard>
                        )}
                      />
                    }
                  />
                  <Route
                    path="markets/:id/practices"
                    element={
                      <AuthenticationGuard
                        component={() => (
                          <RoutePermissionGuard requiredPermission="canEditInsurance">
                            <MarketPracticeManagement />
                          </RoutePermissionGuard>
                        )}
                      />
                    }
                  />
                  <Route
                    path="states/:stateName"
                    element={
                      <AuthenticationGuard
                        component={StateDetailPage}
                      />
                    }
                  />
                  <Route
                    path="plans/:id"
                    element={
                      <AuthenticationGuard component={PlanDetails} />
                    }
                  />
                  <Route
                    path="plans/bulk-edit"
                    element={
                      <AuthenticationGuard
                        component={() => (
                          <RoutePermissionGuard requiredPermission="canEditInsurance">
                            <BulkEditPlans />
                          </RoutePermissionGuard>
                        )}
                      />
                    }
                  />
                  <Route
                    path="search/:q"
                    element={
                      <AuthenticationGuard
                        component={UnifiedSearchResults}
                      />
                    }
                  />
                  <Route
                    path="profile"
                    element={
                      <AuthenticationGuard component={ProfilePage} />
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <AuthenticationGuard component={SettingsPage} />
                    }
                  />
                  <Route
                    path="privacy-policy"
                    element={<PrivacyPolicyPage />}
                  />
                  <Route
                    path="cookie-policy"
                    element={<CookiePolicyPage />}
                  />
                  <Route
                    path="plans/create"
                    element={
                      <AuthenticationGuard
                        component={() => (
                          <RoutePermissionGuard requiredPermission="canEditInsurance">
                            <CreatePlan />
                          </RoutePermissionGuard>
                        )}
                      />
                    }
                  />
                  <Route
                    path="plans/edit/:id"
                    element={
                      <AuthenticationGuard
                        component={() => (
                          <RoutePermissionGuard requiredPermission="canEditInsurance">
                            <CreatePlan />
                          </RoutePermissionGuard>
                        )}
                      />
                    }
                  />
                  <Route
                    path="plans/:id/edit-group"
                    element={
                      <AuthenticationGuard
                        component={() => (
                          <RoutePermissionGuard requiredPermission="canEditInsurance">
                            <EditPlanGroup />
                          </RoutePermissionGuard>
                        )}
                      />
                    }
                  />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
          <DeploymentNotification />
        </DeploymentDetector>
      </SettingsProvider>
    </ChakraProvider>
  );
}
